import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Sun, Zap, Bell, AlertTriangle, ChevronRight } from 'lucide-react';
import { useAuth } from '../auth';
import { authHeaders } from '../auth';
import PlantCard from '../components/ui/PlantCard';
import { LoadingState, EmptyState } from '../components/ui/States';
import './pages.css';

const API_URL = process.env.REACT_APP_API_URL || '';
const API_BASE = `${API_URL}/api`;

const SEVERITY_LABELS = { INFO: 'Information', WARNING: 'Avertissement', CRITICAL: 'Critique' };

// Auto-scales a real measured power value to the most readable SI unit -
// pure display formatting (W/kW/MW is how any real installation's power is
// normally read), not a status judgment or invented threshold.
function formatPower(watts) {
  if (watts >= 1e6) return { value: (watts / 1e6).toFixed(2), unit: 'MW' };
  if (watts >= 1e3) return { value: (watts / 1e3).toFixed(2), unit: 'kW' };
  return { value: watts.toFixed(1), unit: 'W' };
}

// This is the main operational supervision view: a compact summary an
// operator can read in a few seconds, plus links to the detailed pages
// (Plant Detail, Production Forecast, Environmental Impact, History,
// Alerts, Devices, System Health) for anything that needs deeper analysis.
// Every number here is either real telemetry already returned by /api/plants
// (already scoped server-side to this exact user - admin sees every plant,
// a normal user only their assignments, via userManagementService.
// filterPlantsForUser) or the same /api/alerts feed the Alerts page itself
// uses (also already scoped). No admin-only endpoint (/api/admin/devices,
// /api/admin/system-health) is called here - those must never back a
// normal-user view. No new fetch was added beyond widening the existing
// alerts request's limit; plant data comes entirely from the outlet
// context's `plants`, already fetched by App.js for every other page.
export default function DashboardPage() {
  const { plants, socket } = useOutletContext();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // No status filter - both active and resolved come back (same as
    // before), just a higher limit (matches AlertsPage's own 100) since this
    // single fetch now also drives the active-count/severity/attention
    // sections below, not just the compact recent-alerts list.
    fetch(`${API_BASE}/alerts?limit=100`, { headers: authHeaders(token) })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => { if (!cancelled) setAlerts(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setAlerts([]); });
    return () => { cancelled = true; };
  }, [token]);

  // Live delivery via the shared Socket.IO connection (same socket used for
  // sensor_data/weather_data elsewhere - no second connection is opened
  // here). Merges by id like the Alerts page: a brand-new alert is
  // prepended, an existing one being resolved is updated in place.
  useEffect(() => {
    if (!socket) return;
    const onAlertUpdate = (incoming) => {
      setAlerts((prev) => {
        const list = prev || [];
        const idx = list.findIndex((a) => a.id === incoming.id);
        return idx === -1 ? [incoming, ...list] : list.map((a, i) => (i === idx ? incoming : a));
      });
    };
    socket.on('alert_update', onAlertUpdate);
    return () => socket.off('alert_update', onAlertUpdate);
  }, [socket]);

  const safePlants = useMemo(() => (Array.isArray(plants) ? plants : []), [plants]);
  const safeAlerts = useMemo(() => (Array.isArray(alerts) ? alerts : []), [alerts]);

  const plantNameById = useMemo(() => {
    const map = new Map();
    safePlants.forEach((p) => map.set(p.plant_id, p.name || p.plant_id));
    return map;
  }, [safePlants]);

  const alertPlantLabel = (alert) => {
    if (!alert.plant_id) return 'Centrale inconnue';
    return plantNameById.get(alert.plant_id) || alert.plant_id;
  };

  // Active alerts only, real severity values already produced by the
  // backend (alertRuleService) - never invented categories.
  const activeAlerts = useMemo(() => safeAlerts.filter((a) => !a.test && a.status === 'active'), [safeAlerts]);
  const activeCritical = useMemo(() => activeAlerts.filter((a) => a.severity === 'CRITICAL').length, [activeAlerts]);
  const activeWarning = useMemo(() => activeAlerts.filter((a) => a.severity === 'WARNING').length, [activeAlerts]);

  // Panel/station aggregates: summed directly from /api/plants's per-plant
  // detected_panel_count/online_panel_count/detected_station_count/
  // online_station_count - already-scoped real values, no re-derivation of
  // status here (that logic lives once, server-side, against the
  // admin-configured offline-timeout alert rules).
  const detectedPanels = useMemo(() => safePlants.reduce((s, p) => s + (p.detected_panel_count || 0), 0), [safePlants]);
  const onlinePanels = useMemo(() => safePlants.reduce((s, p) => s + (p.online_panel_count || 0), 0), [safePlants]);
  const detectedStations = useMemo(() => safePlants.reduce((s, p) => s + (p.detected_station_count || 0), 0), [safePlants]);
  const onlineStations = useMemo(() => safePlants.reduce((s, p) => s + (p.online_station_count || 0), 0), [safePlants]);

  // Instantaneous production: a real measured 0 W (onlinePanels > 0, every
  // online panel genuinely reporting 0) must never look the same as "no
  // panel is currently online at all" - the same measured-zero-vs-no-data
  // distinction already applied to Énergie Aujourd'hui/Utilisation
  // Instantanée on Plant Detail, using the same online_panel_count signal.
  const totalPowerW = useMemo(() => safePlants.reduce((s, p) => s + (p.total_power || 0), 0), [safePlants]);
  const hasCurrentProductionTelemetry = onlinePanels > 0;
  const formattedPower = hasCurrentProductionTelemetry ? formatPower(totalPowerW) : null;

  // A plant appears here only for concrete, already-existing operational
  // facts - never an invented health score. Each reason is independently
  // true/false from real data already available on the plant object plus
  // this page's own alerts fetch.
  const attentionPlants = useMemo(() => {
    return safePlants
      .map((p) => {
        const plantActiveAlerts = activeAlerts.filter((a) => a.plant_id === p.plant_id);
        const worstSeverity = plantActiveAlerts.some((a) => a.severity === 'CRITICAL')
          ? 'CRITICAL'
          : plantActiveAlerts.some((a) => a.severity === 'WARNING') ? 'WARNING' : null;
        const installedPanelCount = p.installed_panel_count; // number | null - admin config, distinct from detected/online below
        const detectedPanelCount = p.detected_panel_count || 0;
        const onlinePanelCount = p.online_panel_count || 0;
        const detectedStationCount = p.detected_station_count || 0;
        const onlineStationCount = p.online_station_count || 0;

        const reasons = [];
        if (plantActiveAlerts.length > 0) {
          reasons.push(`${plantActiveAlerts.length} alerte${plantActiveAlerts.length > 1 ? 's' : ''} active${plantActiveAlerts.length > 1 ? 's' : ''}${worstSeverity ? ` (${SEVERITY_LABELS[worstSeverity].toLowerCase()})` : ''}`);
        }
        // Zero detected panels is only a genuine operational incident when
        // the admin has actually configured how many panels this plant is
        // supposed to have (installed_panel_count) - a brand-new plant with
        // no installation metadata yet (installed_panel_count === null) is
        // provisioning/configuration uncertainty, not a confirmed failure,
        // and must not be silently treated as one. installed_panel_count is
        // admin-entered metadata (PlantsAdminPage), detected_panel_count is
        // telemetry-known devices (this file's own header comment on
        // renderInstallationSummary in PlantDetailPage.js draws the exact
        // same distinction) - never conflated here either.
        if (typeof installedPanelCount === 'number' && installedPanelCount > 0 && detectedPanelCount === 0) {
          reasons.push('Aucune télémétrie de panneau reçue');
        } else if (detectedPanelCount > 0 && onlinePanelCount < detectedPanelCount) {
          reasons.push(`${detectedPanelCount - onlinePanelCount} panneau${detectedPanelCount - onlinePanelCount > 1 ? 'x' : ''} hors ligne`);
        }
        if (detectedStationCount > 0 && onlineStationCount < detectedStationCount) {
          reasons.push('Station météo hors ligne');
        }

        return {
          plant: p,
          reasons,
          activeAlertCount: plantActiveAlerts.length,
          worstSeverity,
          detectedPanelCount,
          onlinePanelCount,
          detectedStationCount,
          onlineStationCount
        };
      })
      .filter((entry) => entry.reasons.length > 0);
  }, [safePlants, activeAlerts]);

  // Active alerts take operational priority (grouped first), recency within
  // each group - identical ordering rule to AlertsPage.js, not a second
  // convention. Capped to a compact count for the dashboard widget.
  const recentAlerts = useMemo(() => {
    return [...safeAlerts.filter((a) => !a.test)]
      .sort((a, b) => {
        if (a.status !== b.status) return a.status === 'active' ? -1 : 1;
        return new Date(b.triggeredAt) - new Date(a.triggeredAt);
      })
      .slice(0, 6);
  }, [safeAlerts]);

  return (
    <div className="page">
      <div className="page-header">
        <h1><Sun size={22} className="icon-sun" /> Tableau de Bord</h1>
      </div>

      <div className="section">
        <div className="summary-grid">
          <div className="summary-card">
            <div className="summary-label">Centrales supervisées</div>
            <div className="summary-value">{safePlants.length}</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">Alertes actives</div>
            <div className="summary-value">{alerts === null ? '…' : activeAlerts.length}</div>
            {alerts !== null && (activeCritical > 0 || activeWarning > 0) && (
              <div className="summary-sub">
                {activeCritical > 0 && `${activeCritical} critique${activeCritical > 1 ? 's' : ''}`}
                {activeCritical > 0 && activeWarning > 0 && ' · '}
                {activeWarning > 0 && `${activeWarning} avertissement${activeWarning > 1 ? 's' : ''}`}
              </div>
            )}
          </div>
          <div className="summary-card">
            <div className="summary-label">Panneaux</div>
            <div className="summary-value">{onlinePanels} <span className="summary-unit">/ {detectedPanels} en ligne</span></div>
            {detectedPanels > onlinePanels && <div className="summary-sub">{detectedPanels - onlinePanels} hors ligne</div>}
          </div>
          <div className="summary-card">
            <div className="summary-label">Stations météo</div>
            {detectedStations > 0 ? (
              <>
                <div className="summary-value">{onlineStations} <span className="summary-unit">/ {detectedStations} en ligne</span></div>
                {detectedStations > onlineStations && <div className="summary-sub">{detectedStations - onlineStations} hors ligne</div>}
              </>
            ) : (
              <div className="summary-value" style={{ fontSize: '1rem' }}>Aucune détectée</div>
            )}
          </div>
          <div className="summary-card">
            <div className="summary-label">Production Instantanée</div>
            {formattedPower ? (
              <div className="summary-value">{formattedPower.value}<span className="summary-unit"> {formattedPower.unit}</span></div>
            ) : (
              <div className="summary-value" style={{ fontSize: '1rem' }}>Aucune donnée</div>
            )}
          </div>
        </div>
      </div>

      <div className="section">
        <h3 className="section-title"><AlertTriangle size={16} /> Centrales nécessitant une attention</h3>
        {alerts === null ? (
          <LoadingState label="Analyse de l'état des centrales..." />
        ) : attentionPlants.length === 0 ? (
          <EmptyState label="Aucune centrale ne nécessite d'attention actuellement." />
        ) : (
          <div className="summary-grid">
            {attentionPlants.map(({ plant, reasons, activeAlertCount, worstSeverity, detectedPanelCount, onlinePanelCount, detectedStationCount, onlineStationCount }) => (
              <div
                key={plant.plant_id}
                className="summary-card"
                style={{ cursor: 'pointer', borderLeft: `3px solid ${worstSeverity === 'CRITICAL' ? '#c0392b' : worstSeverity === 'WARNING' ? '#d1a24a' : '#666'}` }}
                onClick={() => navigate(`/plants/${plant.plant_id}`)}
                role="button"
                tabIndex={0}
              >
                <div className="summary-label" style={{ color: '#ddd', textTransform: 'none', fontSize: '0.95rem', fontWeight: 600 }}>{plant.name || plant.plant_id}</div>
                <ul style={{ margin: '6px 0 0', paddingLeft: 18, color: '#bbb', fontSize: '0.82rem' }}>
                  {reasons.map((r) => <li key={r}>{r}</li>)}
                </ul>
                <div className="summary-sub" style={{ marginTop: 8 }}>
                  {activeAlertCount > 0 && `${activeAlertCount} alerte(s) · `}
                  Panneaux {onlinePanelCount}/{detectedPanelCount} en ligne
                  {detectedStationCount > 0 && ` · Station ${onlineStationCount}/${detectedStationCount} en ligne`}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="section">
        <div className="section-header-row">
          <h3 className="section-title"><Bell size={16} /> Alertes récentes</h3>
          <button className="link-button" onClick={() => navigate('/alerts')}>Voir tout <ChevronRight size={14} /></button>
        </div>
        {alerts === null ? (
          <LoadingState label="Chargement des alertes..." />
        ) : recentAlerts.length === 0 ? (
          <EmptyState label="Aucune alerte récente" />
        ) : (
          <ul className="alert-list-compact">
            {recentAlerts.map((a) => (
              <li key={a.id} className={`alert-item alert-${(a.severity || 'info').toLowerCase()}`}>
                <div className="alert-row-main">
                  <div className="alert-row-header">
                    <span className="alert-plant-tag">{alertPlantLabel(a)}</span>
                    <span className="alert-sep">•</span>
                    <span className="alert-title">{a.title}</span>
                    <span className={`status-badge status-badge-${a.status}`}>{a.status === 'active' ? 'ACTIVE' : 'RÉSOLUE'}</span>
                  </div>
                  <span className="alert-message">{a.message}</span>
                </div>
                <span className="alert-time">{a.triggeredAt ? new Date(a.triggeredAt).toLocaleString('fr-FR') : ''}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="section">
        <h3 className="section-title"><Zap size={16} /> Mes Centrales</h3>
        {safePlants.length === 0 ? (
          <EmptyState label="Aucune centrale accessible pour votre compte" />
        ) : (
          <div className="plants-grid">
            {safePlants.map((plant) => (
              <PlantCard key={plant.plant_id} plant={plant} onClick={() => navigate(`/plants/${plant.plant_id}`)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
